import React, { useState, useEffect, useCallback } from 'react';
import { Box, Tabs, Tab, Paper, Button, TextField, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { getIcon } from '../../../utils/IconHandler';
import SitesService from '../../../services/SitesService';
import { hasStoredPermission } from '../../../utils/dashboardPermissions';
import ProductsTab from './tabs/ProductsTab';
import CategoriesTab from './tabs/CategoriesTab';
import TagsTab from './tabs/TagsTab';

const Products = () => {
  const { t } = useTranslation();
  const canManageSites = hasStoredPermission('MANAGE_SITES');
  const [activeTab, setActiveTab] = useState(0);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [error, setError] = useState(null);

  // Cargar categorías
  const loadCategories = useCallback(async () => {
    try {
      setLoadingCategories(true);
      const data = await SitesService.getSiteProductCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError(err.response?.data?.message || 'Error al cargar categorías');
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  // Cargar tags
  const loadTags = useCallback(async () => {
    try {
      setLoadingTags(true);
      const data = await SitesService.getSiteProductTags();
      setTags(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading tags:', err);
      setError(err.response?.data?.message || 'Error al cargar tags');
    } finally {
      setLoadingTags(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
    loadTags();
  }, [loadCategories, loadTags]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError(null);
  };

  return (
    <Box p={3}>
      <PageHeader 
        title={t('site_products') || 'Productos del Sitio'} 
        subtitle={t('site_products_subtitle') || 'Gestiona los productos, categorías y tags del sitio web'} 
      />

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mt: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={t('products') || 'Productos'} />
          {canManageSites && <Tab label={t('categories') || 'Categorías'} />}
          {canManageSites && <Tab label={t('tags') || 'Tags'} />}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <ProductsTab
              categories={categories}
              tags={tags}
              onCategoriesChange={loadCategories}
              onTagsChange={loadTags}
            />
          )}
          {canManageSites && activeTab === 1 && (
            <CategoriesTab
              categories={categories}
              onCategoriesChange={loadCategories}
            />
          )}
          {canManageSites && activeTab === 2 && (
            <TagsTab
              tags={tags}
              onTagsChange={loadTags}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Products;
